import Image, { type ImageProps } from '@creator/ui/components/Image/Image';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import { ensureTokenUrl, getDomainConfigValue } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getTokenLogo } from '@src/model/token/helpers';
import { storeEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';

export interface TokenLogoProps {
    className?: string;
    tokenName: string;
    isLoading?: boolean;
}

const TokenLogo: FC<TokenLogoProps> = props => {
    const { tokenName, className, isLoading } = props;

    const token = useStoreState(state => state.token.getToken(tokenName), storeEqualityFn);
    const src = getImageSrc();
    const navigate = useNavigate();

    const onClick = useCallback(() => {
        const defaultTokenName = getDomainConfigValue('defaultTokenName');
        if (defaultTokenName === tokenName) return navigate('/');
        navigate(`/${ensureTokenUrl(tokenName)}`);
    }, [tokenName]);

    function getImageProps(): ImageProps {
        return {
            src,
            className: cn('shrink-0 w-auto', className),
            isLoading,
            onClick,
        };
    }

    function getImageSrc(): string {
        if (token && !token.logoImageURI) return null;
        if (token?.logoImageURI) return token.logoImageURI;

        return getTokenLogo(tokenName);
    }

    if (!src)
        return (
            <Text data-ssr="token-title" onClick={onClick} className={cn('font-medium cursor-pointer content-center', className)}>
                {token?.title}
            </Text>
        );

    return <Image {...getImageProps()} />;
};

export default memo(TokenLogo);
